import { Component, Input } from '@angular/core';
import {
    RegisterEntryOnMissionSummary,
    RpaCategoryGroup
} from '@flyfreely-portal-ui/flyfreely';
import { combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
    findMostPressingRegisterStatus,
    MissionEditService,
    PilotRegisterStatusUnion
} from '../../mission-edit.service';

/**
 * An inline status indicator component that displays an overall status from a list of register statuses.
 * It receives the list of pilot mission register statuses from the mission edit service.
 * It displays the most pressing of these statuses.
 * Is used in the mission editor to show a status summary for each pilot's mission register statuses.
 */
@Component({
    selector: 'pilot-register-details-overall-status',
    template: `
        <span
            *ngIf="overallStatus !== 'NONE' && overallStatus !== 'N/A'"
            class="status-block"
            [ngClass]="overallStatus | registerStatusClass"
        >
            {{ overallStatus | formatMissionEditRegisterStatus }}
        </span>
        <span *ngIf="overallStatus == 'NONE'" class="status-block pending">
            None
        </span>
        <span *ngIf="overallStatus == 'N/A'"> N/A </span>
    `
})
export class PilotRegisterDetailsOverallStatusIndicatorComponent {
    @Input() pilotId: number;
    @Input() status: PilotRegisterStatusUnion;
    @Input() authorityId: number;
    overallStatus: RpaCategoryGroup.RegisterStatus | 'NONE' | 'N/A' = 'NONE';

    entries: RegisterEntryOnMissionSummary[];
    rpaIds: number[] = [];

    private ngUnsubscribe$ = new Subject<void>();
    constructor(private missionEditService: MissionEditService) {}

    ngOnInit() {
        combineLatest([
            this.missionEditService.registerEntriesOnMission$.pipe(
                startWith(null as RegisterEntryOnMissionSummary[])
            ),
            this.missionEditService.missionForm.controls.craftIds.valueChanges.pipe(
                startWith([] as number[])
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([entries, rpaIds]) => {
                this.entries = entries;
                this.rpaIds = rpaIds;
                this.findOverallRegisterStatus();
            });
    }

    ngOnChanges() {
        this.findOverallRegisterStatus();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findOverallRegisterStatus() {
        if (this.authorityId == null || this.status === 'N/A') {
            this.overallStatus = this.authorityId == null ? 'NONE' : 'N/A';
            return;
        }

        const entry =
            this.entries != null
                ? this.entries.find(
                      e =>
                          e.entityId === this.pilotId &&
                          e.authorityId === this.authorityId
                  )
                : null;

        if (
            entry == null ||
            entry.registerStatusGroupList == null ||
            entry.registerStatusGroupList.length === 0
        ) {
            if (this.status != null && this.rpaIds.length === 0) {
                // Fall back on the overall register status if available and there's no mission specific status due to no RPA on the mission
                this.overallStatus = this.status as
                    | RpaCategoryGroup.RegisterStatus
                    | 'NONE';
                return;
            }
            this.overallStatus = 'NONE';
            return;
        }
        this.overallStatus = entry.registerStatusGroupList.reduce(
            (acc, group) =>
                findMostPressingRegisterStatus(group.registerStatus, acc),
            RpaCategoryGroup.RegisterStatus.AUTO
        );
    }
}
